/**
 * NapProspectsModal — shows non-completed NI/MIG subscribers on the same street
 * + barangay + municipality as an installed NAP subscriber, so a NAP with free
 * ports can be matched to nearby pending/cancelled/rescheduled subscribers.
 *
 * Props:
 *   napCode  - the NAP code that was clicked (for the header)
 *   loading  - true while the prospects are being fetched
 *   data     - API response { municipality, barangay, street, street_matched, count, rows }
 *   onClose  - close the modal
 */

function SourceBadge({ source }) {
  const ni = source === "NI";
  return (
    <span
      className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${
        ni ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
      }`}
    >
      {source}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  let cls = "bg-gray-100 text-gray-700";
  if (s.includes("cancel")) cls = "bg-red-100 text-red-700";
  else if (s.includes("resched")) cls = "bg-amber-100 text-amber-700";
  else if (s.includes("pending")) cls = "bg-sky-100 text-sky-700";
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${cls}`}>{status || "—"}</span>;
}

export default function NapProspectsModal({ napCode, loading, data, onClose }) {
  const rows = data?.rows || [];

  return (
    <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/40 p-4" onMouseDown={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Nearby Prospects</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Non-completed subscribers near NAP <span className="font-semibold text-gray-700">{napCode}</span>
            </p>
            {data && (
              <p className="text-xs text-gray-500 mt-1">
                {data.street_matched ? (
                  <>Street <span className="font-medium text-gray-700">“{data.street}”</span> · </>
                ) : (
                  <span className="text-amber-600">No street detected — showing whole barangay · </span>
                )}
                {data.barangay}, {data.municipality}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="overflow-auto">
          {loading ? (
            <div className="px-5 py-12 text-center text-gray-500">Searching nearby subscribers…</div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-12 text-center text-gray-400">
              No non-completed subscribers found on this street/barangay.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700 w-16">Source</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 min-w-[160px]">Subscriber Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 w-28">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 w-36">Contact #</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 min-w-[140px]">Reference</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 min-w-[140px]">Plan</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 min-w-[240px]">Address</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={`${r.source}-${r.id}`} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-3 py-2 text-center align-top"><SourceBadge source={r.source} /></td>
                    <td className="px-3 py-2 align-top font-medium text-gray-800">{r.name || "—"}</td>
                    <td className="px-3 py-2 align-top"><StatusBadge status={r.status} /></td>
                    <td className="px-3 py-2 align-top text-gray-700">{r.contact || "—"}</td>
                    <td className="px-3 py-2 align-top text-gray-700">{r.reference || "—"}</td>
                    <td className="px-3 py-2 align-top text-gray-700">{r.plan || "—"}</td>
                    <td className="px-3 py-2 align-top text-gray-700">{r.address || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {loading ? "" : `${rows.length} prospect(s)`}
          </span>
          <button
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-4 py-1.5 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
